// LogoEditor.jsx
// Created: 2026-03-09
// Project: Giving Words to Voices
// Author: Michael Schwarz
// Version: 1.0.2
//
// Self-contained logo editor component.
// Drop into the Settings modal in App.js.
// Persists all values to localStorage under the key 'gwv_logoConfig'.
//
// INTEGRATION NOTES FOR APP.JS:
//   1. Import this component:
//        import LogoEditor from './LogoEditor';
//
//   2. Add <LogoEditor /> inside the existing {showSettings && (...)} panel,
//      below the keyboard shortcut list.
//
//   3. To consume saved values elsewhere in App.js (e.g. the header logo),
//      read them at startup with:
//        const saved = JSON.parse(localStorage.getItem('gwv_logoConfig') || '{}');
//      and pass them as props to whatever renders the LogoMark.
//
//   4. The component is fully self-contained — no external state or props required.

import { useState, useEffect, useCallback } from 'react';
import Button from './components/ui/Button';

// ── DEFAULTS (canonical 44px spec) ─────────────────────────────────────────
const DEFAULTS = {
  size:        44,
  strokeWidth: 1.5,
  barWidth:    2.2,
  color:       '#1a1a1a',
};

const STORAGE_KEY = 'gwv_logoConfig';
const LEGACY_STORAGE_KEY = 'logoConfig'; // pre-gwv_ key, migrated on first read

// Reads the persisted config, one-time migrating any value still stored under the
// old un-prefixed key so existing custom logos survive the rename.
function readStoredConfig() {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) {
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacy != null) {
        localStorage.setItem(STORAGE_KEY, legacy);
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        raw = legacy;
      }
    }
    return { ...DEFAULTS, ...JSON.parse(raw || '{}') };
  } catch {
    return { ...DEFAULTS };
  }
}

// Geometry normalized from the approved SVG lockup so the shared mark matches
// the dimensionally-correct source assets used elsewhere in the project.
const LOGO_GEOMETRY = {
  circleRadius: 20.8,
  bars: [
    { x: -12.45, h: 3.3, opacity: 0.20 },
    { x: -9.35, h: 5.3, opacity: 0.40 },
    { x: -6.25, h: 9.4, opacity: 0.60 },
    { x: -3.15, h: 14.3, opacity: 0.90 },
    { x: -0.05, h: 16.7, opacity: 1.00 },
    { x:  3.05, h: 14.3, opacity: 0.90 },
    { x:  6.15, h: 9.4, opacity: 0.60 },
    { x:  9.25, h: 5.3, opacity: 0.40 },
    { x: 12.35, h: 3.3, opacity: 0.20 },
  ],
};

// ── LOGO MARK ───────────────────────────────────────────────────────────────
// viewBox is locked to the 44px coordinate space regardless of rendered size.
// strokeWidth and barWidth are passed in the viewBox coordinate space (not pixels).
function LogoMark({ size, strokeWidth, barWidth, color }) {
  const barMidpoints = LOGO_GEOMETRY.bars.map(b => b.x + 2.2 / 2);

  return (
    <svg width={size} height={size} viewBox="-22 -22 44 44" fill="none">
      <circle
        cx="0" cy="0" r={LOGO_GEOMETRY.circleRadius}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
      />
      {LOGO_GEOMETRY.bars.map((b, i) => (
        <rect
          key={i}
          x={barMidpoints[i] - barWidth / 2}
          y={-(b.h / 2)}
          width={barWidth}
          height={b.h}
          rx={barWidth / 2}
          fill={color}
          opacity={b.opacity}
        />
      ))}
    </svg>
  );
}

// ── SLIDER ──────────────────────────────────────────────────────────────────
function Slider({ label, value, min, max, step, onChange, display, theme }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 600,
          color: theme.textSub,
          fontFamily: "'Inter', -apple-system, sans-serif",
          letterSpacing: '0.3px',
        }}>{label}</span>
        <span style={{
          fontSize: 11, fontWeight: 700,
          color: theme.text,
          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
        }}>{display ?? value}</span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        aria-label={label}
        aria-valuetext={display ?? value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: '#007aff', cursor: 'pointer' }}
      />
    </div>
  );
}

// ── COLOUR SWATCHES ─────────────────────────────────────────────────────────
const PRESETS = [
  { label: 'Ink',        value: '#1a1a1a' },
  { label: 'Dark Mode',  value: '#ffffff' },
  { label: 'Accent Red', value: '#e50914' },
  { label: 'Blue',       value: '#007aff' },
  { label: 'Green',      value: '#34c759' },
  { label: 'Amber',      value: '#ff9f0a' },
];

// Picks black or white so a mark drawn on top of `hex` stays legible.
function contrastInk(hex) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.6 ? '#1a1a1a' : '#ffffff';
}

// ── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function LogoEditor({ theme }) {
  // theme tokens passed in from App.js so the panel matches the current mode
  const t = theme || {
    bg:      '#f5f5f7',
    card:    '#f0f0f2',
    text:    '#1a1a1a',
    textSub: '#6e6e73',
    border:  '#d2d2d7',
    inputBg: '#ffffff',
  };

  const [cfg, setCfg] = useState(readStoredConfig);

  // Persist on every change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  }, [cfg]);

  const set = useCallback((key, val) => {
    setCfg(prev => ({ ...prev, [key]: val }));
  }, []);

  const reset = () => {
    const next = { ...DEFAULTS };
    setCfg(next);
    // The old removeItem here was dead: the [cfg] effect immediately re-wrote the
    // defaults back. Worse, reset never told AppLogoMark, so the live header logo
    // kept the old values until the user also pressed the apply button.
    // AppLogoMark's listener ignores event.newValue and re-reads localStorage, and
    // dispatchEvent runs synchronously — before the [cfg] effect commits — so the
    // write must happen here, ahead of the dispatch, exactly as apply does.
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new StorageEvent('storage', {
      key: STORAGE_KEY,
      newValue: JSON.stringify(next),
      storageArea: localStorage,
    }));
  };

  const previewBg = cfg.color === '#ffffff' ? '#1a1a1a' : t.bg;

  return (
    <div style={{ marginTop: 24, borderTop: `1px solid ${t.border}`, paddingTop: 20 }}>

      {/* Section header */}
      <div style={{
        fontFamily: "-apple-system, sans-serif",
        fontSize: 9, fontWeight: 600, letterSpacing: '1.4px',
        color: t.textSub, marginBottom: 20,
        textTransform: 'uppercase',
      }}>
        Logo Editor
      </div>

      {/* Preview canvas */}
      <div style={{
        background: previewBg,
        border: `1px solid ${t.border}`,
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
        marginBottom: 24,
        minHeight: 160,
        transition: 'background 0.2s',
        gap: 16,
      }}>
        <LogoMark
          size={cfg.size}
          strokeWidth={cfg.strokeWidth}
          barWidth={cfg.barWidth}
          color={cfg.color}
        />
        {/* Wordmark preview */}
        <div style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: Math.round(cfg.size * 0.5),
          fontWeight: 400,
          letterSpacing: '0.4px',
          color: cfg.color,
          lineHeight: 1.2,
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}>
          Giving Words to Voices™
        </div>
      </div>

      {/* Sliders */}
      <Slider
        label="Logo Size"
        value={cfg.size}
        min={32} max={512} step={1}
        display={`${cfg.size}px`}
        onChange={v => set('size', v)}
        theme={t}
      />
      <Slider
        label="Circle Stroke"
        value={cfg.strokeWidth}
        min={0.5} max={6} step={0.1}
        display={cfg.strokeWidth.toFixed(1)}
        onChange={v => set('strokeWidth', v)}
        theme={t}
      />
      <Slider
        label="Bar Width"
        value={cfg.barWidth}
        min={0.8} max={5} step={0.1}
        display={cfg.barWidth.toFixed(1)}
        onChange={v => set('barWidth', v)}
        theme={t}
      />

      {/* Colour picker + presets */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 11, fontWeight: 600,
          color: t.textSub,
          fontFamily: "'Inter', -apple-system, sans-serif",
          letterSpacing: '0.3px',
          marginBottom: 10,
        }}>Icon Color</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {PRESETS.map(p => {
            const isSelected = cfg.color === p.value;
            return (
              <button
                key={p.value}
                title={p.label}
                aria-label={p.label}
                aria-pressed={isSelected}
                onClick={() => set('color', p.value)}
                style={{
                  width: 36, height: 36,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 0,
                  borderRadius: '50%',
                  background: p.value,
                  border: isSelected
                    ? '2px solid #007aff'
                    : `2px solid ${p.value === '#ffffff' ? t.border : 'transparent'}`,
                  cursor: 'pointer',
                  flexShrink: 0,
                  boxShadow: isSelected ? '0 0 0 3px #007aff33' : 'none',
                  transition: 'box-shadow 0.15s',
                }}
              >
                {/* Shape-based selection cue so the active swatch is readable without relying on hue */}
                {isSelected && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                    stroke={contrastInk(p.value)} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
          {/* Custom colour input */}
          <div style={{ position: 'relative', width: 36, height: 36 }}>
            <input
              type="color"
              value={cfg.color}
              onChange={e => set('color', e.target.value)}
              style={{
                position: 'absolute', inset: 0,
                opacity: 0, width: '100%', height: '100%',
                cursor: 'pointer',
              }}
              title="Custom colour"
              aria-label="Custom colour"
            />
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: `conic-gradient(red, yellow, lime, cyan, blue, magenta, red)`,
              border: `2px solid ${t.border}`,
              pointerEvents: 'none',
            }} />
          </div>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11, fontWeight: 700,
            color: t.text, marginLeft: 4,
          }}>{cfg.color}</span>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10 }}>
        <Button
          theme={t}
          variant="secondary"
          onClick={reset}
          style={{ flex: 1 }}>
          RESET TO DEFAULT
        </Button>
        <button
          onClick={() => {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
            // Fire storage event manually so AppLogoMark updates in the same tab
            window.dispatchEvent(new StorageEvent('storage', {
              key: STORAGE_KEY,
              newValue: JSON.stringify(cfg),
              storageArea: localStorage,
            }));
          }}
          style={{
            flex: 1, padding: '9px 0',
            background: '#007aff',
            color: '#ffffff',
            border: 'none',
            borderRadius: 7,
            fontFamily: "'Inter', -apple-system, sans-serif",
            fontSize: 11, fontWeight: 700,
            letterSpacing: '0.6px',
            cursor: 'pointer',
          }}>
          APPLY TO HEADER
        </button>
      </div>
    </div>
  );
}

// ── EXPORT: LOGO MARK (for use in App.js header) ────────────────────────────
// Import and use this wherever the logo renders in the app.
// It reads from localStorage automatically at mount time.
//
//   import { AppLogoMark } from './LogoEditor';
//   <AppLogoMark />
//
export function AppLogoMark({ fallbackSize = 44 }) {
  const [cfg, setCfg] = useState(readStoredConfig);

  // Re-read if another tab or component saves
  useEffect(() => {
    const onStorage = () => {
      setCfg(readStoredConfig());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <LogoMark
      size={cfg.size ?? fallbackSize}
      strokeWidth={cfg.strokeWidth ?? DEFAULTS.strokeWidth}
      barWidth={cfg.barWidth ?? DEFAULTS.barWidth}
      color={cfg.color ?? DEFAULTS.color}
    />
  );
}
